import { Tooltip, Typography } from '@mui/material';
import SMediaLinkedinIcon from '../../../assets/icons/ln.svg?react';
import SMediaTwitterIcon from '../../../assets/icons/tw.svg?react';
import SMediaFacebookIcon from '../../../assets/icons/fb.svg?react';
import SMediaCrunchbaseIcon from '../../../assets/icons/Crunchbase.svg?react';
import SMediaPitchbookIcon from '../../../assets/icons/pitchbook.svg?react';
import { css } from '@emotion/react';
import { colors } from '../../../theme/colors';
import { ISourcingProfileData } from '../data/sourcingProfileMockData';
import { panelSurfaceCss } from './panelStyles';

const panelCss = css`
  padding: 12px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const titleCss = css`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.15px;
  color: ${colors.text.main};
`;

const contentCss = css`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const cellCss = css`
  display: flex;
  flex-direction: column;
  flex: 1 0 0;
  min-width: 0;
`;

const cellLabelCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.secondary};
  white-space: nowrap;
`;

const cellValueCss = css`
  font-size: 14px;
  line-height: 1.43;
  letter-spacing: 0.17px;
  padding: 2px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const linkCss = css`
  ${cellValueCss.styles}
  color: ${colors.primary[60]};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const plainTextCss = css`
  ${cellValueCss.styles}
  color: ${colors.text.main};
`;

const iconsRowCss = css`
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 2px 0;
  height: 24px;
`;

const iconLinkCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 100px;
  overflow: hidden;
  flex-shrink: 0;
  text-decoration: none;
`;

interface ContactsWebPanelProps {
  readonly data: Pick<
    ISourcingProfileData,
    | 'website'
    | 'email'
    | 'phone'
    | 'linkedinUrl'
    | 'twitterUrl'
    | 'facebookUrl'
    | 'crunchbaseUrl'
    | 'pitchbookUrl'
  >;
}

export function ContactsWebPanel({ data }: ContactsWebPanelProps) {
  return (
    <div css={[panelSurfaceCss, panelCss]}>
      <Typography css={titleCss}>Contacts &amp; Web</Typography>
      <div css={contentCss}>
        <div css={cellCss}>
          <Typography css={cellLabelCss}>Website</Typography>
          {data.website ? (
            <a
              css={linkCss}
              href={data.website.startsWith('http') ? data.website : `https://${data.website}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              {data.website}
            </a>
          ) : (
            <Typography css={plainTextCss}>—</Typography>
          )}
        </div>

        <div css={cellCss}>
          <Typography css={cellLabelCss}>Email</Typography>
          {data.email ? (
            <a css={linkCss} href={`mailto:${data.email}`}>
              {data.email}
            </a>
          ) : (
            <Typography css={plainTextCss}>—</Typography>
          )}
        </div>

        <div css={cellCss}>
          <Typography css={cellLabelCss}>Phone</Typography>
          <Typography css={plainTextCss}>{data.phone ?? '—'}</Typography>
        </div>

        <div css={cellCss}>
          <Typography css={cellLabelCss}>Profiles</Typography>
          <div css={iconsRowCss}>
            {data.linkedinUrl && (
              <ProfileIconLink href={data.linkedinUrl} label='LinkedIn'>
                <SMediaLinkedinIcon width={20} height={20} />
              </ProfileIconLink>
            )}
            {data.pitchbookUrl && (
              <ProfileIconLink href={data.pitchbookUrl} label='PitchBook'>
                <SMediaPitchbookIcon width={20} height={20} />
              </ProfileIconLink>
            )}
            {data.crunchbaseUrl && (
              <ProfileIconLink href={data.crunchbaseUrl} label='Crunchbase'>
                <SMediaCrunchbaseIcon width={20} height={20} />
              </ProfileIconLink>
            )}
            {data.twitterUrl && (
              <ProfileIconLink href={data.twitterUrl} label='Twitter / X'>
                <SMediaTwitterIcon width={20} height={20} />
              </ProfileIconLink>
            )}
            {data.facebookUrl && (
              <ProfileIconLink href={data.facebookUrl} label='Facebook'>
                <SMediaFacebookIcon width={20} height={20} />
              </ProfileIconLink>
            )}
            {!data.linkedinUrl &&
              !data.pitchbookUrl &&
              !data.crunchbaseUrl &&
              !data.twitterUrl &&
              !data.facebookUrl && <Typography css={plainTextCss}>—</Typography>}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileIconLink({
  href,
  label,
  children,
}: {
  readonly href: string;
  readonly label: string;
  readonly children: React.ReactNode;
}) {
  return (
    <Tooltip title={label}>
      <a css={iconLinkCss} href={href} target='_blank' rel='noopener noreferrer' aria-label={label}>
        {children}
      </a>
    </Tooltip>
  );
}
